<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import LaisChecklistPanel from 'dashboard/components-next/LaisChecklistPanel.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  gate: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
  reviewLoading: { type: Boolean, default: false },
  errorMessage: { type: String, default: '' },
  referenceNow: { type: [String, Number, Date], default: undefined },
});

const emit = defineEmits(['review']);

const { t } = useI18n();

const reason = ref('');
const selectedItemKey = ref('');

const gateItems = computed(() => props.gate?.items || []);
const blockers = computed(() => props.gate?.blockers || []);
const permissions = computed(() => props.gate?.permissions || {});
const ledger = computed(() => props.gate?.ledger || []);
const canWaive = computed(() => permissions.value.can_waive === true);
const canReview = computed(() => permissions.value.can_review !== false);
const isBlocked = computed(() => props.gate?.can_advance === false);
const isReady = computed(() => props.gate?.can_advance === true);

const evidenceLabel = item => {
  const count = Number(item?.evidence_count || 0);
  if (!count) return t('PROPERTY_OFFERS.KYC_DILIGENCE.NO_EVIDENCE');
  return t('PROPERTY_OFFERS.KYC_DILIGENCE.EVIDENCE_COUNT', { count });
};

const checklistItems = computed(() =>
  gateItems.value.map(item => ({
    id: item.key,
    label: item.label,
    status: item.ui_status || 'pending',
    owner: item.domain,
    evidenceRedacted: true,
    evidenceLabel: evidenceLabel(item),
    blocker: item.reason,
  }))
);

const selectableItems = computed(() =>
  (blockers.value.length ? blockers.value : gateItems.value).filter(
    item => item?.key
  )
);

const selectedItem = computed(
  () =>
    selectableItems.value.find(item => item.key === selectedItemKey.value) ||
    selectableItems.value[0] ||
    null
);

const reasonReady = computed(() => reason.value.trim().length >= 5);
const reviewReady = computed(
  () => canReview.value && selectedItem.value && reasonReady.value
);

const statusTone = computed(() => {
  if (isReady.value) return 'positive';
  if (isBlocked.value) return 'danger';
  return 'warning';
});

const statusLabel = computed(() => {
  if (isReady.value) return t('PROPERTY_OFFERS.KYC_DILIGENCE.STATUS.READY');
  if (isBlocked.value) return t('PROPERTY_OFFERS.KYC_DILIGENCE.STATUS.BLOCKED');
  return t('PROPERTY_OFFERS.KYC_DILIGENCE.STATUS.REVIEW');
});

const formatDate = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const blockerText = blocker =>
  [blocker?.label, blocker?.reason].filter(Boolean).join(': ');

const ledgerTitle = entry =>
  [entry?.domain, entry?.decision].filter(Boolean).join(' / ');

const submitReview = decision => {
  if (!reviewReady.value || props.reviewLoading) return;
  emit('review', {
    item_key: selectedItem.value.key,
    decision,
    decision_reason: reason.value.trim(),
  });
  reason.value = '';
};

watch(
  selectableItems,
  items => {
    if (!items.length) {
      selectedItemKey.value = '';
      return;
    }
    if (!items.some(item => item.key === selectedItemKey.value)) {
      selectedItemKey.value = items[0].key;
    }
  },
  { immediate: true }
);
</script>

<template>
  <section
    class="grid gap-4 rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 shadow-lais-card sm:p-5"
  >
    <header class="flex flex-wrap items-start justify-between gap-3">
      <div class="min-w-0">
        <div class="flex items-center gap-2">
          <span
            class="i-lucide-shield-check size-5 shrink-0 text-lais-primary"
            aria-hidden="true"
          />
          <h2
            class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
          >
            {{ t('PROPERTY_OFFERS.KYC_DILIGENCE.TITLE') }}
          </h2>
        </div>
        <p class="mt-1 text-lais-body-sm text-lais-text-muted">
          {{ t('PROPERTY_OFFERS.KYC_DILIGENCE.DESCRIPTION') }}
        </p>
      </div>
      <LaisStatusBadge :label="statusLabel" :tone="statusTone" />
    </header>

    <div
      v-if="errorMessage"
      class="rounded-lais-sm border border-rose-100 bg-rose-50 px-3 py-2 text-lais-body-sm text-rose-800"
      role="alert"
    >
      {{ errorMessage }}
    </div>

    <div
      v-if="isBlocked && blockers.length"
      class="rounded-lais-sm border border-rose-100 bg-rose-50 px-3 py-2 text-lais-body-sm text-rose-800"
      role="alert"
    >
      <p class="font-semibold">
        {{ t('PROPERTY_OFFERS.KYC_DILIGENCE.BLOCKED_TITLE') }}
      </p>
      <ul class="mt-1 grid gap-1">
        <li v-for="blocker in blockers" :key="blocker.key">
          {{ blockerText(blocker) }}
        </li>
      </ul>
    </div>

    <LaisChecklistPanel
      :title="t('PROPERTY_OFFERS.KYC_DILIGENCE.CHECKLIST')"
      :items="checklistItems"
      :loading="loading"
      :reference-now="referenceNow"
      redacted
    />

    <div class="grid gap-3 rounded-lais-sm border border-lais-border-soft p-3">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <p
          class="text-lais-label-sm font-semibold uppercase text-lais-text-muted"
        >
          {{ t('PROPERTY_OFFERS.KYC_DILIGENCE.REVIEW_TITLE') }}
        </p>
        <span
          v-if="!canWaive"
          class="inline-flex items-center gap-1 text-lais-body-xs text-lais-text-muted"
        >
          <span class="i-lucide-lock size-3.5" aria-hidden="true" />
          {{ t('PROPERTY_OFFERS.KYC_DILIGENCE.WAIVER_LOCKED') }}
        </span>
      </div>

      <label class="grid gap-1 text-lais-body-sm">
        <span class="font-semibold text-lais-text">
          {{ t('PROPERTY_OFFERS.KYC_DILIGENCE.ITEM') }}
        </span>
        <select
          v-model="selectedItemKey"
          class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-lais-body-sm text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
          :disabled="!selectableItems.length || reviewLoading"
        >
          <option
            v-for="item in selectableItems"
            :key="item.key"
            :value="item.key"
          >
            {{ item.label }}
          </option>
        </select>
      </label>

      <label class="grid gap-1 text-lais-body-sm">
        <span class="font-semibold text-lais-text">
          {{ t('PROPERTY_OFFERS.KYC_DILIGENCE.REASON') }}
        </span>
        <textarea
          v-model="reason"
          rows="3"
          class="resize-none rounded-lais-sm border border-lais-border bg-white px-3 py-2 text-lais-body-sm text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
          :placeholder="t('PROPERTY_OFFERS.KYC_DILIGENCE.REASON_PLACEHOLDER')"
          :disabled="reviewLoading"
        />
      </label>

      <div class="flex flex-wrap gap-2">
        <LaisPrimaryButton
          size="sm"
          icon="i-lucide-check"
          :disabled="!reviewReady"
          :is-loading="reviewLoading"
          @click="submitReview('approved')"
        >
          {{ t('PROPERTY_OFFERS.KYC_DILIGENCE.APPROVE') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          size="sm"
          variant="ghost"
          icon="i-lucide-x"
          :disabled="!reviewReady"
          :is-loading="reviewLoading"
          @click="submitReview('rejected')"
        >
          {{ t('PROPERTY_OFFERS.KYC_DILIGENCE.REJECT') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          size="sm"
          variant="ghost"
          icon="i-lucide-shield-alert"
          :disabled="!reviewReady || !canWaive"
          :is-loading="reviewLoading"
          @click="submitReview('waived')"
        >
          {{ t('PROPERTY_OFFERS.KYC_DILIGENCE.WAIVE') }}
        </LaisPrimaryButton>
      </div>
    </div>

    <div v-if="ledger.length" class="grid gap-2">
      <p
        class="text-lais-label-sm font-semibold uppercase text-lais-text-muted"
      >
        {{ t('PROPERTY_OFFERS.KYC_DILIGENCE.LEDGER') }}
      </p>
      <ol class="grid gap-2">
        <li
          v-for="entry in ledger.slice(0, 6)"
          :key="entry.id"
          class="rounded-lais-sm bg-lais-bg-soft px-3 py-2 text-lais-body-xs text-lais-text-body"
        >
          <div class="flex flex-wrap items-center justify-between gap-2">
            <span class="font-semibold text-lais-text">
              {{ ledgerTitle(entry) }}
            </span>
            <span class="text-lais-text-muted">
              {{ formatDate(entry.occurred_at) }}
            </span>
          </div>
          <p class="mt-1 text-lais-text-muted">
            {{ entry.human_reason || entry.decision_reason }}
          </p>
        </li>
      </ol>
    </div>
  </section>
</template>
